import type { AxiosError } from 'axios'
import { store } from '@/app/store'
import { resetAuthState, resetContextData } from '@/store/slices/auth.slice'
import { resetPermissions } from '@/store/slices/permissions.slice'
import { resetNavigation } from '@/store/slices/navigation.slice'
import { tokenManager } from '@/utils/tokenManager'
import { UrlGenerator } from '@/utils/url-generator.util'

function readApiMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined
  const root = data as Record<string, unknown>
  if (typeof root.message === 'string') return root.message
  if (root.data && typeof root.data === 'object') {
    const nested = root.data as Record<string, unknown>
    if (typeof nested.message === 'string') return nested.message
  }
  return undefined
}

/** JWT/session references a user id that no longer exists (or authorizer cannot load user). */
export function isUserNotFoundApiError(error: unknown): boolean {
  const ax = error as AxiosError<{ message?: string; success?: boolean; code?: string }>
  const status = ax?.response?.status
  if (status !== 401 && status !== 403 && status !== 404) return false

  const message = readApiMessage(ax?.response?.data)
  const code = (ax?.response?.data as { code?: string } | undefined)?.code
  if (code === 'USER_NOT_FOUND') return true
  if (!message) return false
  return message.trim().toLowerCase() === 'user not found'
}

/** Clear client session and redirect to logout (runs even when relaxed auto-logout is on). */
export function forceLogoutInvalidSession(): void {
  if (typeof window === 'undefined') return
  if (sessionStorage.getItem('logoutInProgress')) return

  try {
    store.dispatch(resetAuthState())
    store.dispatch(resetContextData())
    store.dispatch(resetPermissions())
    store.dispatch(resetNavigation())
  } catch {
    // ignore
  }

  sessionStorage.setItem('logoutInProgress', 'true')
  tokenManager.clearAllAuthData()
  try {
    sessionStorage.clear()
    sessionStorage.setItem('logoutInProgress', 'true')
  } catch {
    //
  }

  window.location.replace(UrlGenerator.getBaseLogoutUrl())
}
